import { Injectable } from "@angular/core";
import * as dayjs from "dayjs";
import { Language } from "../../enums/language.enum";
import { LorenSession } from "../loren-authentication-service/constants/loren-user-session.constants";
import { LorenUserInfo } from "../../interfaces/loren-user-info.interface";
import { LorenDashboardWidgetPreference } from "../../../features/loren-dashboard/interfaces/loren-dashboard-widget-preference.interface";
import { LorenDashboardCardsViewTypes } from "../../../features/loren-dashboard/components/loren-dashboard/enums/loren-dashboard-cards-view-types.enum";
import { LorenAsset } from "../../../features/loren-assets/interfaces/loren-asset.interface";
import { LorenMetering } from "../../../features/loren-assets/interfaces/loren-metering.interface";
import { LorenGeofencingZone } from "../../../features/loren-geofencing/interfaces/loren-geofencing-zone.interface";
import "dayjs/locale/bg";

@Injectable({
  providedIn: "root",
})
export class LorenUtilsService {

  static generateArrayToGETParams<T>(propertyName: string, stringArr: T[]) {
    let result = "";

    stringArr.forEach((x, index) => {
      result += propertyName + "=" + x;
      if (index + 1 < stringArr.length) {
        result += "&";
      }
    });

    return result;
  }

  static setMomentLanguageLocale(currentLang: string) {
    if (currentLang === Language.BG) {
      dayjs.locale("bg");
    } else {
      dayjs.locale("en");
    }
  }

  static updateLastSelectedLanguage(lang: string) {
    localStorage.setItem(LorenSession.lastSelectedLanguageKey, lang);
    }

  static getLastSelectedLanguage() {
        return localStorage.getItem(LorenSession.lastSelectedLanguageKey);
    }

  static getDashboardWidgetsPreferences(
    userId: string
  ): Map<number, LorenDashboardWidgetPreference> {
    const key = `${LorenSession.dashboardWidgetsKey}-${userId}`;
    const preferences = localStorage.getItem(key);
    if (preferences) {
      return new Map(JSON.parse(preferences));
    }

    return null;
  }

  static setDashboardWidgetsPreferences(
    userId: string,
    preferences: Map<number, LorenDashboardWidgetPreference>
  ) {
    const key = `${LorenSession.dashboardWidgetsKey}-${userId}`;
    return localStorage.setItem(
      key,
      JSON.stringify(Array.from(preferences.entries()))
    );
  }

  static setDashboardWidgetViewIndexPreference(
    assetId: number,
    viewIndex: number,
    userId: string
  ) {
      const key = `${LorenSession.assetsViewsKey}-${userId}`;
      const preferences = localStorage.getItem(key);
      let map: Map<number, number> = new Map<number, number>();
      if (preferences) {
      map = new Map(JSON.parse(preferences));
    }

      map.set(assetId, viewIndex);
      localStorage.setItem(
        key,
      JSON.stringify(Array.from(map.entries()))
    );
  }

  static getDashboardWidgetViewIndexPreference(assetId: number, userId: string): number {
      const key = `${LorenSession.assetsViewsKey}-${userId}`;
      const preferences = localStorage.getItem(key);
      if (preferences) {
      const map = new Map<number, number>(JSON.parse(preferences));
      return map.get(assetId);
    } else {
      return null;
    }
  }

  static setDashboardMapPreference(preference: boolean, userId: string) {
      const key = `${LorenSession.mapPreference}-${userId}`;
      localStorage.setItem(key, preference ? "true" : "false"
    );
  }

  static getDashboardMapPreference( userId: string): boolean {
      const key = `${LorenSession.mapPreference}-${userId}`;
      return localStorage.getItem(key) !== "false";
  }

  static getSelectedAssets(userId: string) {
    const key = `${LorenSession.selectedAssets}-${userId}`;
    let string = localStorage.getItem(key);

    if (string === null || JSON.parse(string).length === 0) {
      // Remove this after 2026-01-01
      // Try to fetch legacy key
      const oldKey = LorenSession.selectedAssets;
      string = localStorage.getItem(oldKey);

      // Old key found - migrate to new key
      if (string !== null) {
        localStorage.setItem(key, string);
      }
    }

    if (string === null) {
      return null;
    }

    return JSON.parse(string) as number[];
  }

  static setDashboardFilteredGroups(userId: string, groups: number[]) {
    const key = `${LorenSession.groups}-${userId}`;
    localStorage.setItem(key, JSON.stringify(groups));
  }

  static setSelectedAssets(selectedAssets: number[], userId: string) {
      const key = `${LorenSession.selectedAssets}-${userId}`;
      localStorage.setItem(
        key,
      JSON.stringify(selectedAssets)
    );
  }

  static updateLastUserTour() {
    localStorage.setItem(
      LorenSession.userPassedTour,
      JSON.stringify(new Date())
    );
  }

  static getLastUserTour() {
    return localStorage.getItem(LorenSession.userPassedTour);
  }

  static hasUserPassedTour() {
    return this.getLastUserTour() !== null;
  }

  static generateRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  static generateRandomBoolean() {
    return Math.random() >= 0.5;
  }

  static generateRandomString(length) {
    let result = "";
    const characters =
      "ABCDEFG HIJKLMNOP QRSTUVWXY Zabcdefghijklmnopqrstuvwxyz01 23456789";
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
  }

  static resolveUserDisplayName(userInfo: LorenUserInfo): string {
    if (!userInfo) {
      return "";
    }
    if (userInfo.firstName && userInfo.lastName) {
      return `${userInfo.firstName} ${userInfo.lastName}`;
    } else if (userInfo.firstName && !userInfo.lastName) {
      return `${userInfo.firstName}`;
    } else if (!userInfo.firstName && userInfo.lastName) {
      return `${userInfo.lastName}`;
    } else {
      return userInfo.username;
    }
  }

  static generateRandomEntities<T>(
    type: new () => T,
    numberOfEntities: number
  ): T[] {
    const result: T[] = [];

    for (let i = 0; i < numberOfEntities; i++) {
      const instance = new type();
      result.push(instance);
    }

    return result;
  }

  static setDashboardLastSelectedViewType(
    selectedView: LorenDashboardCardsViewTypes,
    userId
  ) {
    const key = `${LorenSession.dashboardViewTypeKey}-${userId}`;
    return localStorage.setItem(key, String(selectedView));
  }

  static getDashboardLastSelectedViewType(userId) {
    const key = `${LorenSession.dashboardViewTypeKey}-${userId}`;
    return localStorage.getItem(key);
  }

  static getAssetFromMeteringId(assets: LorenAsset[], id: number) {
    return assets?.find((a) => a.metering.some((m) => m.id === id));
  }

  static getMeteringFromId(meterings: LorenMetering[], id: number) {
    return meterings?.find((m) => m.id === id);
  }

  static getGeozoneFromId(geozones: LorenGeofencingZone[], id: number) {
    return geozones?.find((g) => g.id === id);
  }
}
